// ============================================================
// Staff Management Service
// ============================================================

import { departmentsApi } from '@/api/departments.api';
import { designationsApi } from '@/api/designations.api';
import { staffApi } from '@/api/staff.api';
import { staffDocumentsApi } from '@/api/staffDocuments.api';
import { leaveTypesApi } from '@/api/leaveTypes.api';
import { staffDisciplineApi } from '@/api/staffDiscipline.api';

// Helper to normalize array returns from backend API
function normalizeList(data, key) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data[key])) return data[key];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export const staffManagementService = {
  // ── Departments ──────────────────────────────────────────
  getDepartments: async (schoolId, params = {}) => {
    const res = await departmentsApi.getAll(schoolId, params);
    return normalizeList(res, 'departments');
  },

  getDepartmentById: async (id, schoolId) => {
    return await departmentsApi.getById(id, schoolId);
  },

  createDepartment: async (schoolId, payload) => {
    return await departmentsApi.create(schoolId, payload);
  },

  updateDepartment: async (id, schoolId, payload) => {
    return await departmentsApi.update(id, schoolId, payload);
  },

  deleteDepartment: async (id, schoolId) => {
    return await departmentsApi.delete(id, schoolId);
  },

  // ── Designations ─────────────────────────────────────────
  getDesignations: async (schoolId, params = {}) => {
    const res = await designationsApi.getAll(schoolId, params);
    return normalizeList(res, 'designations');
  },

  getDesignationById: async (id, schoolId) => {
    return await designationsApi.getById(id, schoolId);
  },

  createDesignation: async (schoolId, payload) => {
    return await designationsApi.create(schoolId, payload);
  },

  updateDesignation: async (id, schoolId, payload) => {
    return await designationsApi.update(id, schoolId, payload);
  },

  deleteDesignation: async (id, schoolId) => {
    return await designationsApi.delete(id, schoolId);
  },

  // ── Staff ────────────────────────────────────────────────
  getStaffList: async (schoolId, params = {}) => {
    const res = await staffApi.getAll(schoolId, params);
    return normalizeList(res, 'staff');
  },

  getStaffById: async (id, schoolId) => {
    const res = await staffApi.getById(id, schoolId);
    return res?.staff || res?.data || res;
  },

  getStaffProfile: async (id, schoolId) => {
    const res = await staffApi.getProfile(id, schoolId);
    return res?.profile || res?.staff || res?.data || res;
  },

  createStaff: async (schoolId, payload) => {
    const res = await staffApi.create(schoolId, payload);
    return res?.staff || res?.data || res;
  },

  updateStaff: async (id, schoolId, payload) => {
    const res = await staffApi.update(id, schoolId, payload);
    return res?.staff || res?.data || res;
  },

  updateStaffStatus: async (id, schoolId, employmentStatus) => {
    const res = await staffApi.updateStatus(id, schoolId, employmentStatus);
    return res?.staff || res?.data || res;
  },

  deleteStaff: async (id, schoolId) => {
    const res = await staffApi.delete(id, schoolId);
    return res?.data || res;
  },

  // ── Staff Documents ──────────────────────────────────────
  getStaffDocuments: async (staffId, schoolId) => {
    const res = await staffDocumentsApi.getAll(staffId, schoolId);
    return normalizeList(res, 'documents');
  },

  getStaffDocumentById: async (documentId, staffId, schoolId) => {
    const res = await staffDocumentsApi.getById(documentId, staffId, schoolId);
    return res?.document || res?.data || res;
  },

  createStaffDocument: async (staffId, schoolId, documentData) => {
    const res = await staffDocumentsApi.create(staffId, schoolId, documentData);
    return res?.document || res?.data || res;
  },

  updateStaffDocument: async (documentId, staffId, schoolId, documentData) => {
    const res = await staffDocumentsApi.update(documentId, staffId, schoolId, documentData);
    return res?.document || res?.data || res;
  },

  deleteStaffDocument: async (documentId, staffId, schoolId) => {
    const res = await staffDocumentsApi.delete(documentId, staffId, schoolId);
    return res?.data || res;
  },

  // ── Leave Types ─────────────────────────────────────────
  getLeaveTypes: async (schoolId, params = {}) => {
    const res = await leaveTypesApi.getAll(schoolId, params);
    return normalizeList(res, 'leave_types');
  },

  getLeaveTypeById: async (id, schoolId) => {
    return await leaveTypesApi.getById(id, schoolId);
  },

  createLeaveType: async (schoolId, payload) => {
    return await leaveTypesApi.create(schoolId, payload);
  },

  updateLeaveType: async (id, schoolId, payload) => {
    return await leaveTypesApi.update(id, schoolId, payload);
  },

  deleteLeaveType: async (id, schoolId) => {
    return await leaveTypesApi.delete(id, schoolId);
  },

  // ── Staff Discipline ─────────────────────────────────────
  getDisciplineStatusEnums: async () => {
    return await staffDisciplineApi.getStatusEnums();
  },

  getStaffDisciplineHistory: async (staffId, schoolId) => {
    return await staffDisciplineApi.getDisciplineHistory(staffId, schoolId);
  },

  updateStaffDiscipline: async (staffId, schoolId, payload) => {
    return await staffDisciplineApi.updateDiscipline(staffId, schoolId, payload);
  },
};
